import { unique } from 'lib/array/arrayUtils'
import { adjustFullName } from 'lib/flights/flightUtils'

const titleGenderMap: Record<string, string> = {
  Mr: 'Male',
  Ms: 'Female',
  Mrs: 'Female',
  Miss: 'Female',
  Master: 'Male',
}

const flightsTitleConversionMap: Record<string, string> = {
  Master: 'mstr',
}

interface OrderBaggage {
  id: string;
  departing: { count: number };
  returning: { count: number };
}

export function buildBaggagePayload(
  departingBaggage: App.Checkout.BaggageQuantityMap = {},
  returningBaggage: App.Checkout.BaggageQuantityMap = {},
): Array<OrderBaggage> {
  const ids = unique([
    ...Object.keys(departingBaggage),
    ...Object.keys(returningBaggage),
  ])

  return ids.map((id) => ({
    id,
    departing: {
      count: departingBaggage[id] || 0,
    },
    returning: {
      count: returningBaggage[id] || 0,
    },
  }))
}

function transformTitle(title: string) {
  return flightsTitleConversionMap[title] ?? title.toLowerCase()
}

/**
 * Format the passenger request object
 *
 * @remarks
 * Some pax attributes are mandatory, even though we do not surface them in the traveller detail forms.
 * In these cases, fallback to another form - a sensible choice is that of the primary contact's
 */
export function buildPassengerPayload(
  pax: App.Checkout.FlightPassenger,
  form: App.Checkout.TravellerForm,
  fallbackForm: App.Checkout.TravellerForm | undefined,
  baggagePayload: Array<OrderBaggage>,
  carrierCodes: Array<string>,
  travellerSchema: App.Checkout.TravellerFormSchema | undefined,
) {
  const passportFields = form.data.passportNumber ? {
    passport_number: form.data.passportNumber,
    country_of_issue: form.data.countryOfIssue,
    nationality: form.data.nationality,
    passport_expiry_date: form.data.passportExpiry,
  } : null

  const title = (pax.type === 'adult' ? form.data.title : form.data.minorTitle) || form.data.title || 'Mr'
  const gender = titleGenderMap[title]

  const fullNameField = travellerSchema?.properties ? Object.values(travellerSchema.properties).find(p => p.type === 'flightPassengerNameField') : undefined
  const nameAdjustmentType = fullNameField?.subtype
  const maxTotalLength = fullNameField?.maxLength

  let middleName = form.data.middleName
  let firstName = form.data.firstName
  let lastName = form.data.lastName

  let originalMiddleName: string | undefined = undefined
  let originalFirstName: string | undefined = undefined
  let originalLastName: string | undefined = undefined

  if (nameAdjustmentType && maxTotalLength) {
    const adjustedName = adjustFullName({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      middleName: middleName ?? '',
      nameAdjustmentType,
      maxTotalLength,
    })

    firstName = adjustedName.firstName
    lastName = adjustedName.lastName
    middleName = adjustedName.middleName

    if (form.data.middleName && middleName !== form.data.middleName) {
      originalMiddleName = form.data.middleName
    }

    if (form.data.firstName && firstName !== form.data.firstName) {
      originalFirstName = form.data.firstName
    }

    if (form.data.lastName && lastName !== form.data.lastName) {
      originalLastName = form.data.lastName
    }
  }

  return {
    type: pax.type,
    birth_date: form.data.dateOfBirth,
    given_name: firstName,
    surname: lastName,
    ...(middleName ? { middle_names: [middleName] } : {}),
    ...(originalMiddleName ? { original_middle_names: [originalMiddleName] } : {}),
    ...(originalFirstName ? { original_given_name: originalFirstName } : {}),
    ...(originalLastName ? { original_surname: originalLastName } : {}),
    gender,
    title: transformTitle(title),
    extras: baggagePayload.length > 0 ? { baggage: baggagePayload } : {},
    email: form.data.email || fallbackForm?.data.email,
    post_code: form.data.postcode || fallbackForm?.data.postcode,
    ...loyaltyProgramNumbers(form, carrierCodes),
    ...(passportFields ?? {}),
    ...(form.data.usStateOfResidence ? { state_of_residence: form.data.usStateOfResidence } : {}),
    phone: form.data.phonePrefix ? `${form.data.phonePrefix}${form.data.phone}` : `${fallbackForm?.data.phonePrefix}${fallbackForm?.data.phone}`,
  }
}

function loyaltyProgramNumbers(form: App.Checkout.TravellerForm, carrierCodes: Array<string>) {
  const programNumbers: Array<App.FrequentFlyer> = []

  // For two one-ways there is a possibility of having different carriers for
  // each leg.
  // frequentFlyerAirline1 is departing
  if (form.data.frequentFlyerAirline1 && form.data.frequentFlyerNumber1) {
    programNumbers.push({
      booking_carrier: carrierCodes[0],
      number: form.data.frequentFlyerNumber1,
      program_carrier: form.data.frequentFlyerAirline1,
      is_valid: true,
    })
  }

  // frequentFlyerAirline2 is returning
  if (form.data.frequentFlyerAirline2 && form.data.frequentFlyerNumber2) {
    programNumbers.push({
      booking_carrier: carrierCodes[1],
      number: form.data.frequentFlyerNumber2,
      program_carrier: form.data.frequentFlyerAirline2,
      is_valid: true,
    })
  }

  if (form.data.qantasFrequentFlyerNo) {
    programNumbers.push({
      booking_carrier: 'QF',
      program_carrier: 'QF',
      number: form.data.qantasFrequentFlyerNo,
      is_valid: true,
    })
  }

  if (form.data.velocityFrequentFlyerNo) {
    programNumbers.push({
      booking_carrier: 'VA',
      program_carrier: 'VA',
      number: form.data.velocityFrequentFlyerNo,
      is_valid: true,
    })
  }

  if (programNumbers.length === 0) {
    return {}
  }

  return { loyalty_program_numbers: { frequent_flyer: programNumbers } }
}
